const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const toTitle = (value) =>
  String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

const dateTimeFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const isUnixSeconds = (value) =>
  typeof value === "number" && value > 946684800 && value < 4102444800;

const toDate = (value) => {
  if (isUnixSeconds(value)) return new Date(value * 1000);

  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  return null;
};

export const formatAnnualDate = (value, { includeTime = false } = {}) => {
  const date = toDate(value);
  if (!date) return formatAnnualValue(value);
  return includeTime ? dateTimeFormatter.format(date) : dateFormatter.format(date);
};

export const formatAnnualDateRange = (dateRange = {}) => {
  if (!isPlainObject(dateRange)) return "N/A";

  const start = dateRange.createdFrom;
  const end = dateRange.createdTo;
  if (!start || !end) return "N/A";

  return `${formatAnnualDate(start)} - ${formatAnnualDate(end)}`;
};

export const formatAnnualValue = (value, options = {}) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (options.kind === "date") return formatAnnualDate(value);
  if (options.kind === "datetime") return formatAnnualDate(value, { includeTime: true });
  if (typeof value === "number") {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ");
  if (isPlainObject(value)) return "Available";
  return String(value);
};

export const normalizeAnnualDataQualityEnvelope = (rawData) => {
  const envelope = isPlainObject(rawData?.data) ? rawData.data : rawData;

  if (!isPlainObject(envelope)) {
    return {
      hasEnvelope: false,
      metadata: {},
      confidence: null,
      summaryCards: [],
      tables: [],
      charts: [],
      warnings: [],
      hasRenderableContent: false,
    };
  }

  const summaryCards = Array.isArray(envelope.summaryCards)
    ? envelope.summaryCards.filter(Boolean).map((card, index) => ({
        id: card.id || card.key || `summary-${index}`,
        label: card.label || card.title || toTitle(card.key) || `Metric ${index + 1}`,
        value: card.value ?? card.count ?? card.total ?? "",
        status: card.status || card.tone || "",
        description: card.description || card.detail || card.helpText || "",
      }))
    : [];

  const tables = Array.isArray(envelope.tables)
    ? envelope.tables.filter(Boolean).map((table, tableIndex) => {
        const rows = Array.isArray(table.rows) ? table.rows : [];
        const firstObjectRow = rows.find(isPlainObject);
        const fallbackColumns = firstObjectRow
          ? Object.keys(firstObjectRow).map((key) => ({ id: key, label: toTitle(key) }))
          : [];
        const columns = Array.isArray(table.columns) && table.columns.length
          ? table.columns.map((column) =>
              typeof column === "string"
                ? { id: column, label: toTitle(column) }
                : {
                    id: column.id || column.key || column.accessor || column.label,
                    label: column.label || column.title || toTitle(column.id || column.key),
                  }
            )
          : fallbackColumns;

        return {
          id: table.id || table.key || `table-${tableIndex}`,
          title: table.title || table.label || `Table ${tableIndex + 1}`,
          description: table.description || "",
          columns: columns.filter((column) => column.id),
          rows,
          maxRows: table.maxRows || 12,
        };
      })
    : [];

  const charts = Array.isArray(envelope.charts)
    ? envelope.charts.filter(Boolean).map((chart, chartIndex) => ({
        id: chart.id || chart.key || `chart-${chartIndex}`,
        key: chart.key || chart.id || `chart-${chartIndex}`,
        title: chart.title || chart.label || toTitle(chart.key || `Chart ${chartIndex + 1}`),
        description: chart.description || "",
        type: chart.type || "bar",
        data: Array.isArray(chart.data) ? chart.data : [],
      }))
    : [];
  const warnings = Array.isArray(envelope.warnings) ? envelope.warnings.filter(Boolean) : [];

  return {
    hasEnvelope: true,
    metadata: isPlainObject(envelope.metadata) ? envelope.metadata : {},
    confidence: envelope.confidence ?? null,
    summaryCards,
    tables,
    charts,
    warnings,
    hasRenderableContent: Boolean(
      summaryCards.length ||
        tables.some((table) => table.rows.length) ||
        charts.length ||
        warnings.length ||
        Object.keys(envelope.metadata || {}).length ||
        envelope.confidence !== undefined
    ),
  };
};
